import type { ReactNode } from 'react';
import { cx } from '../cx';
import type { ChartLayout } from './layout';
import { CHART_MINIMUM, LABEL_TEXT, SEQ_FILL, SEQ_TEXT, TICK_TEXT } from './scale';
import { ROUGH_FILL, sampleTier } from './thin';

export type HeatmapProps = {
  rows: Array<{ id: string; label: ReactNode }>;
  columns: Array<{ id: string; label: ReactNode }>;
  /** values[row][column]; null for no data. */
  values: Array<Array<number | null>>;
  /**
   * How many values each cell is computed from: under 10 shows no value, 10 to 19 a faint dashed
   * cell (rough). Leave out for counts, where the value is its own sample.
   */
  samples?: Array<Array<number | null>>;
  /** Five ascending boundaries splitting the six steps of the ramp; defaults to round numbers from the data. */
  steps?: number[];
  format: (value: number) => string;
  /** `auto` (default) switches to the narrow form in a narrow container: shorter cells, a narrower minimum column width and a narrower sticky row-label column. It still scrolls sideways. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

const COLUMN: Record<ChartLayout, string> = { auto: 'min-w-12 @md:min-w-18', narrow: 'min-w-12', wide: 'min-w-18' };
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: 'min-w-16 pr-2 @md:min-w-24 @md:pr-3',
  narrow: 'min-w-16 pr-2',
  wide: 'min-w-24 pr-3',
};
const CELL: Record<ChartLayout, string> = { auto: 'h-6 @md:h-8', narrow: 'h-6', wide: 'h-8' };

/** Five round-number boundaries that split the values into six roughly even steps. */
export function heatSteps(values: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  if (!sorted.length) return [1, 2, 3, 4, 5];
  const round = (v: number) => {
    if (v <= 0) return 0;
    const m = 10 ** Math.floor(Math.log10(v));
    return Math.round(v / m) * m;
  };
  const out: number[] = [];
  for (let k = 1; k <= 5; k++) {
    const v = round(sorted[Math.min(sorted.length - 1, Math.floor((k / 6) * sorted.length))]);
    out.push(Math.max(v, (out[out.length - 1] ?? 0) + Number.EPSILON));
  }
  return out;
}

export const stepOf = (value: number, steps: number[]): number => steps.filter((s) => value >= s).length;

/**
 * A grid of two categories crossed, coloured by the sequential ramp (more is always stronger
 * against the ground), each cell's value written in the ramp's own text colour. Scrolls sideways
 * on narrow screens with the row labels fixed. Rough cells (10 to 19 values) are faint and dashed;
 * say what that means with a ThinDataLegend under the chart.
 */
export function Heatmap({
  rows,
  columns,
  values,
  samples,
  steps,
  format,
  layout = 'auto',
  description,
  className,
}: HeatmapProps) {
  const flat = values.flat().filter((v): v is number => v != null);
  const bounds = steps ?? heatSteps(flat);
  return (
    // A scroll area on narrow screens, so it takes keyboard focus and can be scrolled with the arrow keys.
    <div
      tabIndex={0}
      className={cx(
        '@container overflow-x-auto rounded-jc-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
        className,
      )}
    >
      <div
        role="img"
        aria-label={description}
        className="grid min-w-max gap-0.5"
        style={{ gridTemplateColumns: `auto repeat(${columns.length}, minmax(auto, 1fr))` }}
      >
        <span className={cx('sticky left-0 bg-jc-surface', ROW_LABEL[layout])} />
        {columns.map((c) => (
          <span key={c.id} className={cx('truncate px-1 pb-1 text-center', COLUMN[layout], TICK_TEXT)}>
            {c.label}
          </span>
        ))}
        {rows.map((r, i) => (
          <div key={r.id} className="contents">
            <span
              className={cx('sticky left-0 flex items-center truncate bg-jc-surface', ROW_LABEL[layout], LABEL_TEXT)}
            >
              {r.label}
            </span>
            {columns.map((c, j) => {
              const v = values[i]?.[j] ?? null;
              const n = samples ? (samples[i]?.[j] ?? null) : v;
              const shown = v != null && n != null && n >= CHART_MINIMUM.cell;
              const s = shown ? stepOf(v, bounds) : 0;
              const rough = shown && samples != null && sampleTier(n) === 'rough';
              return (
                <span
                  key={c.id}
                  className={cx(
                    'flex items-center justify-center rounded-jc-info font-display text-xs font-bold tabular-nums',
                    CELL[layout],
                    shown ? cx(SEQ_FILL[s], SEQ_TEXT[s]) : 'bg-jc-elevated',
                    rough && ROUGH_FILL,
                  )}
                >
                  {shown ? format(v) : null}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The ramp's legend, fewer to more, with translated end labels. */
export function HeatmapLegend({ fewer, more }: { fewer: ReactNode; more: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {fewer}
      {SEQ_FILL.map((f) => (
        <span key={f} className={cx('h-2.5 w-5 rounded-jc-info', f)} />
      ))}
      {more}
    </span>
  );
}
